import _ from 'underscore';
import {v4 as uuidv4} from 'uuid';

import {assert, assertAndContinue} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BatchSchema} from 'constants/SampleConstants';
import {RunOutputSamplesTableMetadata} from 'lab-automation/output-samples/RunOutputSamplesTableAdapter';
import {
  FromServerRunOutputSamplesTable,
  RunOutputSamplesTable,
  ToServerRunOutputSamplesTable,
} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import {prepareDataTableForSave, prepareDataTableForStore} from 'notebook/data-tables/DataTableUtil';
import {ResultsTableMetadata} from 'notebook/integrations/results/ResultsTableConstants';
import {
  FromServerResultsTable,
  ResultsTable,
  ToServerResultsTable,
} from 'notebook/integrations/results/ResultsTableConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import getNoteLinkedObjectsOfTypes from 'notebook/notes/util/getNoteLinkedObjectsOfTypes';
import {addCid} from 'notebook/store/StoreTransformUtil';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {doesUserHavePermissionToViewTable} from 'notebook/structured-tables/util/doesUserHavePermissionToViewTable';
import * as StructuredTableStoreTransforms from 'notebook/structured-tables/util/StructuredTableStoreTransforms';
import {uniqueTableName} from 'notebook/tables/excess/Util';
import {Note, NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import {AssayRunSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {Schema} from 'schemas/SchemaConstants';
import APIUtil from 'util/APIUtil';
import {assertNever} from 'util/Types';
import uniqueId from 'util/uniqueId';

import {
  FromServerNLADVersionedData,
  FromServerNoteLinkedAssayData,
  NLADVersionedData,
  NoteLinkedAssayData,
  ToServerNLADVersionedData,
  ToServerNoteLinkedAssayData,
} from './AttachmentConstants';

export function createEmptyNoteLinkedAssayData(
  assayRunSchema: AssayRunSchema
): NoteLinkedAssayData & {type: NoteLinkedObjectType.NOTE_LINKED_ASSAY_DATA} {
  return {
    cid: uniqueId('nlar'),
    type: NoteLinkedObjectType.NOTE_LINKED_ASSAY_DATA,
    assayRunSchema,
    assayRunId: null,
    assayResultIds: [],
    assayResultSchemas: [],
    dataTables: [],
    displayConfigBySchemaId: {},
    attachedForReview: true,
    provenanceUuid: uuidv4(),
    versionedData: {
      _internalVersion: 3,
      runSchemaName: assayRunSchema.name,
      fields: assayRunSchema.fields,
      tags: null,
      structuredTables: [],
      automationInputFiles: null,
      automationOutputFiles: null,
      automationErrorFiles: null,
      connectionId: null,
      equipmentId: null,
    },
  };
}

export function prepareForStore(
  noteLinkedAssayData: FromServerNoteLinkedAssayData,
  cidIdMap: CidIdMap
): void {
  addCid(noteLinkedAssayData, 'nlar', cidIdMap);

  for (const dataTable of noteLinkedAssayData.dataTables) {
    prepareDataTableForStore(dataTable, cidIdMap);
  }
  for (const structuredTable of noteLinkedAssayData.versionedData.structuredTables) {
    StructuredTableStoreTransforms.prepareForStore<RunOutputSamplesTableMetadata | ResultsTableMetadata, {}>(
      structuredTable,
      cidIdMap
    );
  }
}

export function prepareForSave(
  noteLinkedAssayData: NoteLinkedAssayData,
  cidIdMap: CidIdMap
): ToServerNoteLinkedAssayData {
  const dataTables = noteLinkedAssayData.dataTables.map((dataTable) =>
    prepareDataTableForSave(dataTable, cidIdMap)
  );
  const structuredTables = noteLinkedAssayData.versionedData.structuredTables.map((table) =>
    StructuredTableStoreTransforms.prepareForSave<RunOutputSamplesTableMetadata | ResultsTableMetadata, {}>(
      table,
      cidIdMap
    )
  ) as Array<RunOutputSamplesTable>;
  return {
    ...noteLinkedAssayData,
    id: cidIdMap[noteLinkedAssayData.cid] as string,
    dataTables,
    versionedData: {
      ...noteLinkedAssayData.versionedData,
      structuredTables,
    },
  };
}

export function getNoteLinkedAssayDatasInNote(note: Note): Array<NoteLinkedAssayData> {
  return getNoteLinkedObjectsOfTypes(note, [
    ModelTypeConstants.NOTE_LINKED_ASSAY_DATA,
  ]) as Array<NoteLinkedAssayData>;
}

export function getBaseTableNameForOutputSamplesTable(
  outputSamplesTable: FromServerRunOutputSamplesTable
): string {
  const schema = outputSamplesTable.schemas[0];
  // Old output samples tables have a schema
  if (schema != null) {
    // These tables are newly initialized by the server and do not yet have names
    const schemaType = APIUtil.apiIdToType(schema.id);
    if (schemaType === ModelTypeConstants.BATCH_SCHEMA) {
      return `${(schema as BatchSchema).fileTypeName} Batches`;
    } else {
      assert(schemaType === ModelTypeConstants.TAG_SCHEMA, 'Unexpected schema type for output samples table');
      return (schema as Schema).name;
    }
  }
  // Newer tables have no schema; we get the name from the config
  assertAndContinue(
    outputSamplesTable.metadata.configName.length > 0,
    'Output samples tables must have a configName property',
    {outputSamplesTable}
  );
  return outputSamplesTable.metadata.configName;
}

export function getBaseTableNameForResultsTable(resultsTable: FromServerResultsTable): string {
  const schema = resultsTable.schemas[0];
  return `${schema.name}`;
}

export function prepareNLADStructuredTablesForStore(
  structuredTables: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>,
  existingTableNames: Array<string>
): CidIdMap {
  const cidIdMap = {};
  const usedTableNames = [...existingTableNames];
  structuredTables.forEach((structuredTable) => {
    let tableName;
    switch (structuredTable.kind) {
      case StructuredTableKind.RUN_OUTPUT_SAMPLES:
        tableName = getBaseTableNameForOutputSamplesTable(structuredTable);
        break;
      case StructuredTableKind.RESULTS:
        tableName = getBaseTableNameForResultsTable(structuredTable);
        break;
      default:
        assert(false, `Unexpected structured table type in NLAD: ${assertNever(structuredTable).kind}`);
    }
    const newName = uniqueTableName(usedTableNames, tableName, {allowNoSuffix: true});
    usedTableNames.push(newName);
    structuredTable.name = newName;
    StructuredTableStoreTransforms.prepareForStore<{}, {}>(structuredTable, cidIdMap);
  });
  return cidIdMap;
}

type NLADStructuredTable =
  | RunOutputSamplesTable
  | FromServerRunOutputSamplesTable
  | ToServerRunOutputSamplesTable
  | ResultsTable
  | FromServerResultsTable
  | ToServerResultsTable;

export function getOutputSamplesTablesFromVersionedData(
  versionedData: NLADVersionedData
): Array<RunOutputSamplesTable>;
export function getOutputSamplesTablesFromVersionedData(
  versionedData: FromServerNLADVersionedData
): Array<FromServerRunOutputSamplesTable>;
export function getOutputSamplesTablesFromVersionedData(
  versionedData: ToServerNLADVersionedData
): Array<ToServerRunOutputSamplesTable>;
export function getOutputSamplesTablesFromVersionedData(
  versionedData: NLADVersionedData | FromServerNLADVersionedData | ToServerNLADVersionedData
): Array<RunOutputSamplesTable | FromServerRunOutputSamplesTable | ToServerRunOutputSamplesTable> {
  // eslint-disable-next-line no-restricted-syntax
  return _.filter(
    versionedData.structuredTables as Array<NLADStructuredTable>,
    (structuredTable: NLADStructuredTable) =>
      structuredTable.kind === StructuredTableKind.RUN_OUTPUT_SAMPLES &&
      doesUserHavePermissionToViewTable(structuredTable as RunOutputSamplesTable)
  ) as Array<RunOutputSamplesTable | FromServerRunOutputSamplesTable | ToServerRunOutputSamplesTable>;
}

export function getResultsTablesFromVersionedData(versionedData: NLADVersionedData): Array<ResultsTable>;
export function getResultsTablesFromVersionedData(
  versionedData: FromServerNLADVersionedData
): Array<FromServerResultsTable>;
export function getResultsTablesFromVersionedData(
  versionedData: ToServerNLADVersionedData
): Array<ToServerResultsTable>;
export function getResultsTablesFromVersionedData(
  versionedData: NLADVersionedData | FromServerNLADVersionedData | ToServerNLADVersionedData
): Array<ResultsTable | FromServerResultsTable | ToServerResultsTable> {
  // eslint-disable-next-line no-restricted-syntax
  return _.filter(
    versionedData.structuredTables as Array<NLADStructuredTable>,
    (structuredTable) => structuredTable.kind === StructuredTableKind.RESULTS
  ) as Array<ResultsTable | FromServerResultsTable | ToServerResultsTable>;
}

export function cloneNoteLinkedAssayData(noteLinkedAssayData: NoteLinkedAssayData): NoteLinkedAssayData {
  return {
    ...noteLinkedAssayData,
    cid: uniqueId('nlar'),
    provenanceUuid: uuidv4(),
  };
}
